/**
 * 画像編集画面(ImageEditorModal / AiEditModal)の共通処理。
 */
import type { ProductImageOcr } from '@/types/api'
import { ocrBadgeState } from '@/utils/ocrNg'

/** 候補の出どころ。Amazon / AI / ローカル の区分で並べる */
/** pse = 候補の「17番画像」の枠から上げた画像 */
export type CandidateOrigin = 'amazon' | 'ai' | 'local' | 'pse'

/** 画面上の「候補」1枚(一時置き場にある、まだ登録していない画像) */
export interface Candidate {
  id: string
  origin: CandidateOrigin
  stagingKey: string
  url: string
  /** PT01 / 結果1 / ファイル名 */
  label: string
  /** 1500×1500 / #1を修正 など */
  sub: string
  /** 現在の画像との対応(Amazonのみ) */
  match?: { kind: 'same' | 'near'; imageNo: number } | null
  ocr?: ProductImageOcr
  ocrChecking?: boolean
  aiJobId?: string
  /** AIの結果の番号(ジョブ内)。候補の「削除」(非表示)に使う */
  aiIndex?: number
  /** マウスを乗せたときの説明(AIの結果は指示の内容) */
  title?: string
}

/** AI修正の元にする画像 */
export type AiSource =
  | { kind: 'current'; imageNo: number; url: string; ocr?: ProductImageOcr; label: string }
  | { kind: 'cand'; stagingKey: string; url: string; ocr?: ProductImageOcr; label: string }

export interface OcrChip {
  text: string
  cls: string
  title: string
}

/** OCRの状態をサムネイル下の小さな印にする(既存の .ocr-badge のクラスを使う) */
export function ocrChips(ocr: ProductImageOcr | undefined, checking = false, pendingText = 'OCR未'): OcrChip[] {
  if (checking) return [{ text: 'OCR中', cls: 'ocr-badge ocr-badge-pending', title: 'OCRしています' }]
  if (ocr?.status === 'unavailable') return []
  const st = ocrBadgeState(ocr)
  if (!st) return []
  if (st.kind === 'pending') return [{ text: pendingText, cls: 'ocr-badge ocr-badge-pending', title: 'まだOCRされていません' }]
  if (st.kind === 'clean') return [{ text: 'NGなし', cls: 'ocr-badge ocr-badge-clean', title: '画像内にNGワードはありません' }]
  return st.counts.map((c) => ({
    text: `NG${c.count}`,
    cls: `ocr-badge ocr-badge-lv lv${c.level}`,
    title: `画像内のNGワード Lv.${c.level === 5 ? '5以下' : c.level}: ${c.count}件`,
  }))
}

/**
 * 画像の公開URLから、画像バケット内のキーを求める(「兄弟ASINへコピー」のコピー元指定用)。
 * 公開URLは https://<domain>/<prefix>/... で、パス先頭の / を除いたものがキーになる。
 * 外部URL(旧さくらサーバー等)は判定できないためサーバー側で拒否される。
 */
export function keyFromImageUrl(url: string): string | null {
  try {
    const u = new URL(url)
    return decodeURIComponent(u.pathname.replace(/^\/+/, '')) || null
  } catch {
    return null
  }
}

let seq = 0
export function newId(prefix: string): string {
  seq += 1
  return `${prefix}-${Date.now().toString(36)}-${seq}`
}

/** 画像ファイルとして扱えるもの(一時置き場の署名URLが受け付ける形式) */
export const ACCEPT_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
